import React, { useState } from 'react';
import { X, User, Phone, Mail, Calendar, MapPin, CreditCard, Percent, Check } from 'lucide-react';
import { createDriver } from '../../services/api';
import { toast } from 'sonner';

const AddDriverModal = ({ isOpen, onClose, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    birthdate: '',
    address: '',
    city: '',
    id_number: '',
    license_number: '',
    license_expiry: '',
    commission: 15
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.phone || !formData.birthdate || !formData.id_number || !formData.license_number) {
      toast.error('Plotësoni të gjitha fushat e detyrueshme');
      return;
    }

    setLoading(true);
    try {
      await createDriver({
        ...formData,
        status: 'offline',
        permit_status: 'pending',
        rating: 5.0,
        total_rides: 0,
        earnings: 0
      });
      toast.success('Shoferi u shtua me sukses!');
      setFormData({
        name: '', email: '', phone: '', birthdate: '', address: '', city: '',
        id_number: '', license_number: '', license_expiry: '', commission: 15
      });
      onSuccess && onSuccess();
    } catch (error) {
      console.error('Error creating driver:', error);
      toast.error('Gabim gjatë shtimit të shoferit');
    }
    setLoading(false);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      
      <div 
        className="relative bg-white rounded-2xl shadow-2xl w-full max-w-xl mx-4 md:mx-auto"
        style={{ fontFamily: "'Inter Tight', sans-serif" }}
      >
        {/* Header */}
        <div 
          className="px-5 py-3 rounded-t-2xl flex items-center justify-between"
          style={{ background: 'linear-gradient(135deg, #1b1c1b, #2d2e2d)' }}
        >
          <div className="flex items-center gap-2">
            <div className="p-1.5 rounded-lg bg-white/10">
              <User className="w-4 h-4 text-white" />
            </div>
            <h2 className="text-lg font-bold text-white">Shto Shofer të Ri</h2>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-full hover:bg-white/20 transition-colors">
            <X className="w-4 h-4 text-white" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-4">
          {/* Row 1: Name & Birthdate */}
          <div className="grid grid-cols-2 gap-3 mb-3">
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">
                Emri i Plotë <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Emri Mbiemri"
                className="w-full px-3 py-2 rounded-lg border border-gray-200 focus:ring-2 focus:ring-yellow-400 focus:border-transparent text-sm"
                required
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">
                Datëlindja <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                value={formData.birthdate}
                onChange={(e) => setFormData(prev => ({ ...prev, birthdate: e.target.value }))}
                className="w-full px-3 py-2 rounded-lg border border-gray-200 focus:ring-2 focus:ring-yellow-400 focus:border-transparent text-sm"
                required
              />
            </div>
          </div>

          {/* Row 2: Phone & Email */}
          <div className="grid grid-cols-2 gap-3 mb-3">
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">
                Nr. Celular <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <Phone className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                  placeholder="+355 6X XXX XXXX"
                  className="w-full pl-8 pr-3 py-2 rounded-lg border border-gray-200 focus:ring-2 focus:ring-yellow-400 focus:border-transparent text-sm"
                  required
                />
              </div>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">
                Email <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <Mail className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                  placeholder="email@example.com"
                  className="w-full pl-8 pr-3 py-2 rounded-lg border border-gray-200 focus:ring-2 focus:ring-yellow-400 focus:border-transparent text-sm"
                  required
                />
              </div>
            </div>
          </div>

          {/* Row 3: ID Number & License */}
          <div className="grid grid-cols-2 gap-3 mb-3">
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">
                Nr. Personal (ID) <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <CreditCard className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                <input
                  type="text"
                  value={formData.id_number}
                  onChange={(e) => setFormData(prev => ({ ...prev, id_number: e.target.value.toUpperCase() }))}
                  placeholder="I12345678A"
                  className="w-full pl-8 pr-3 py-2 rounded-lg border border-gray-200 focus:ring-2 focus:ring-yellow-400 focus:border-transparent text-sm"
                  required
                />
              </div>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">
                Nr. Patentës <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.license_number}
                onChange={(e) => setFormData(prev => ({ ...prev, license_number: e.target.value.toUpperCase() }))}
                placeholder="AL-12345678"
                className="w-full px-3 py-2 rounded-lg border border-gray-200 focus:ring-2 focus:ring-yellow-400 focus:border-transparent text-sm"
                required
              />
            </div>
          </div>

          {/* Row 4: License Expiry & Commission */}
          <div className="grid grid-cols-2 gap-3 mb-3">
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">
                Skadenca e Patentës <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                value={formData.license_expiry}
                onChange={(e) => setFormData(prev => ({ ...prev, license_expiry: e.target.value }))}
                className="w-full px-3 py-2 rounded-lg border border-gray-200 focus:ring-2 focus:ring-yellow-400 focus:border-transparent text-sm"
                required
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">
                Komisioni (%) <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <Percent className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                <input
                  type="number"
                  value={formData.commission}
                  onChange={(e) => setFormData(prev => ({ ...prev, commission: parseInt(e.target.value) }))}
                  min="0"
                  max="50"
                  className="w-full pl-8 pr-3 py-2 rounded-lg border border-gray-200 focus:ring-2 focus:ring-yellow-400 focus:border-transparent text-sm"
                  required
                />
              </div>
            </div>
          </div>

          {/* Row 5: Address & City */}
          <div className="grid grid-cols-5 gap-3 mb-4">
            <div className="col-span-3">
              <label className="block text-xs font-semibold text-gray-700 mb-1">
                Adresa <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <MapPin className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                <input
                  type="text"
                  value={formData.address}
                  onChange={(e) => setFormData(prev => ({ ...prev, address: e.target.value }))}
                  placeholder="Rruga, Nr."
                  className="w-full pl-8 pr-3 py-2 rounded-lg border border-gray-200 focus:ring-2 focus:ring-yellow-400 focus:border-transparent text-sm"
                  required
                />
              </div>
            </div>
            <div className="col-span-2">
              <label className="block text-xs font-semibold text-gray-700 mb-1">
                Qyteti <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.city}
                onChange={(e) => setFormData(prev => ({ ...prev, city: e.target.value }))}
                className="w-full px-3 py-2 rounded-lg border border-gray-200 focus:ring-2 focus:ring-yellow-400 focus:border-transparent text-sm bg-white"
                required
              >
                <option value="">Zgjidhni...</option>
                <option value="Tiranë">Tiranë</option>
                <option value="Durrës">Durrës</option>
                <option value="Vlorë">Vlorë</option>
                <option value="Shkodër">Shkodër</option>
                <option value="Elbasan">Elbasan</option>
                <option value="Korçë">Korçë</option>
                <option value="Fier">Fier</option>
                <option value="Berat">Berat</option>
              </select>
            </div>
          </div>

          {/* Info Note */}
          <div className="p-2.5 rounded-lg bg-yellow-50 border border-yellow-200 mb-4">
            <p className="text-xs text-yellow-800">
              <strong>Shënim:</strong> Automjetet caktohen nga seksioni "Flota" pasi shoferi të aprovohet.
            </p>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={loading}
            className="w-full py-2.5 rounded-lg font-bold text-black transition-all hover:scale-[1.02] disabled:opacity-50 flex items-center justify-center gap-2 text-sm"
            style={{ background: 'linear-gradient(135deg, #fabc0c, #e0a80a)' }}
          >
            {loading ? (
              <>
                <div className="w-4 h-4 border-2 border-black/30 border-t-black rounded-full animate-spin" />
                Duke shtuar...
              </>
            ) : (
              <>
                <Check className="w-4 h-4" />
                Shto Shoferin
              </>
            )}
          </button>
        </form>
      </div>
    </div>
  );
};

export default AddDriverModal;
